import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Box,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import QuotationRequest from '../components/QuotationRequest';
import CustomerReviews from '../components/CustomerReviews';
import SectionBlock from '../components/SectionBlock';
import Seo from '../components/Seo';

const Quotations = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [quotations, setQuotations] = useState([]);
  const [error, setError] = useState('');
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [showQuotationDialog, setShowQuotationDialog] = useState(false);
  const [showMaidSelection, setShowMaidSelection] = useState(false);
  const [serviceCatalog, setServiceCatalog] = useState([]);
  const apiBase = process.env.REACT_APP_API_URL.replace(/\/$/, '');

  useEffect(() => {
    if (user) {
      fetchQuotations();
      fetchServiceCatalog();
    }
  }, [user]);

  const fetchQuotations = async () => {
    try {
      const response = await fetch(`${apiBase}/api/quotations`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch quotations');
      }
      setQuotations(data);
    } catch (error) {
      setError(error.message);
    }
  };

  const fetchServiceCatalog = async () => {
    try {
      const response = await fetch(`${apiBase}/api/service-catalog`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch service catalog');
      }
      setServiceCatalog(data);
    } catch (error) {
      setError(error.message);
    }
  };

  const handleViewQuotation = (quotation) => {
    if (!user) {
      navigate('/login', { state: { from: '/quotations' } });
      return;
    }
    setSelectedQuotation(quotation);
    setShowQuotationDialog(true);
  };

  const handleAcceptQuotation = async (maidId) => {
    if (!user) {
      navigate('/login', { state: { from: '/quotations' } });
      return;
    }

    try {
      const response = await fetch(
        `${apiBase}/api/quotations/${selectedQuotation.id}/accept`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ maidId })
        }
      );

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to accept quotation');
      }

      setShowMaidSelection(false);
      setShowQuotationDialog(false);
      fetchQuotations();
    } catch (error) {
      setError(error.message);
    }
  };

  const handleRejectQuotation = async () => {
    if (!user) {
      navigate('/login', { state: { from: '/quotations' } });
      return;
    }

    try {
      const response = await fetch(
        `${apiBase}/api/quotations/${selectedQuotation.id}/reject`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to reject quotation');
      }

      setShowQuotationDialog(false);
      fetchQuotations();
    } catch (error) {
      setError(error.message);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING':
        return 'warning';
      case 'ACCEPTED':
        return 'success';
      case 'REJECTED':
        return 'error';
      case 'EXPIRED':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  };

  return (
    <SectionBlock>
      <Seo
        title="Get a Free Cleaning Quote"
        description="Tell us about your home or rental and get a free, no-pressure cleaning quote for Orlando, Kissimmee, Davenport and surrounding areas."
        path="/quotations"
      />
      <Container maxWidth="md">
        <Typography variant="h4" align="center" gutterBottom>
          Request a Detailed Quote
        </Typography>
        <QuotationRequest />
      </Container>
    </SectionBlock>
  );
};

export default Quotations; 